import type { QueryKey } from '@tanstack/react-query'
import { getAssetUrl } from '@/config/assetUrl'

const DEFAULT_ECHO_TIMEOUT_MS = 12_000
const DEFAULT_RETRY_MS = 5_000
const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_REFETCH_MAX_WAIT_MS = 8_000

export type RotationReconciliationEntry = {
  originalSrc: string
  reconcileQueryKey: QueryKey | null
  rotationRequestUUID: string | null
  serverAcknowledged: boolean
  status: 'idle' | 'committing' | 'reconciling'
}

type RotationQueryClient = {
  getQueriesData: (filters: { queryKey: QueryKey }) => Array<[QueryKey, unknown]>
  invalidateQueries: (filters: { queryKey: QueryKey }) => Promise<unknown>
}

type RotationLookup = {
  entry: RotationReconciliationEntry
  mediaObjectId: string
}

type RotationReconcilerOptions = {
  clearForRequest: (rotationRequestUUID: string) => void
  echoTimeoutMs?: number
  getEntryByRequest: (rotationRequestUUID: string) => RotationLookup | undefined
  maxAttempts?: number
  queryClient: RotationQueryClient
  refetchMaxWaitMs?: number
  retryMs?: number
}

type Watchdog = {
  attempts: number
  timer: ReturnType<typeof setTimeout>
}

/**
 * Owns the rotate confirmation race independently of the lightbox lifetime.
 * A Mercure echo is the fast path; a bounded targeted refetch is the fallback.
 */
export class RotationReconciler {
  private readonly clearForRequest: (rotationRequestUUID: string) => void
  private readonly echoTimeoutMs: number
  private readonly getEntryByRequest: (rotationRequestUUID: string) => RotationLookup | undefined
  private readonly maxAttempts: number
  private readonly queryClient: RotationQueryClient
  private readonly refetchMaxWaitMs: number
  private readonly retryMs: number
  private readonly watchdogs = new Map<string, Watchdog>()
  private readonly echoReconciliations = new Set<string>()
  private readonly exhausted = new Set<string>()
  private disposed = false

  constructor(options: RotationReconcilerOptions) {
    this.clearForRequest = options.clearForRequest
    this.echoTimeoutMs = options.echoTimeoutMs ?? DEFAULT_ECHO_TIMEOUT_MS
    this.getEntryByRequest = options.getEntryByRequest
    this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS
    this.queryClient = options.queryClient
    this.refetchMaxWaitMs = options.refetchMaxWaitMs ?? DEFAULT_REFETCH_MAX_WAIT_MS
    this.retryMs = options.retryMs ?? DEFAULT_RETRY_MS
  }

  sync(entries: Record<string, RotationReconciliationEntry>): void {
    if (this.disposed) return

    const activeRequests = new Set(
      Object.values(entries)
        .filter(
          entry =>
            entry.status === 'reconciling' &&
            typeof entry.rotationRequestUUID === 'string' &&
            entry.rotationRequestUUID !== '',
        )
        .map(entry => entry.rotationRequestUUID as string),
    )

    for (const rotationRequestUUID of this.watchdogs.keys()) {
      if (!activeRequests.has(rotationRequestUUID)) {
        this.cancelWatchdog(rotationRequestUUID)
      }
    }
    for (const rotationRequestUUID of this.exhausted) {
      if (!activeRequests.has(rotationRequestUUID)) {
        this.exhausted.delete(rotationRequestUUID)
      }
    }

    for (const rotationRequestUUID of activeRequests) {
      if (
        this.watchdogs.has(rotationRequestUUID) ||
        this.echoReconciliations.has(rotationRequestUUID) ||
        this.exhausted.has(rotationRequestUUID)
      ) {
        continue
      }
      this.scheduleWatchdog(rotationRequestUUID, this.echoTimeoutMs, 0)
    }
  }

  async handleEcho(rotationRequestUUID: string): Promise<void> {
    if (this.disposed || this.echoReconciliations.has(rotationRequestUUID)) return
    const lookup = this.getEntryByRequest(rotationRequestUUID)
    if (!lookup) return

    this.cancelWatchdog(rotationRequestUUID)
    this.exhausted.delete(rotationRequestUUID)
    this.echoReconciliations.add(rotationRequestUUID)
    try {
      await this.refreshParent(lookup.entry)
      if (!this.disposed && this.getEntryByRequest(rotationRequestUUID)) {
        this.clearForRequest(rotationRequestUUID)
      }
    } finally {
      this.echoReconciliations.delete(rotationRequestUUID)
    }
  }

  dispose(): void {
    this.disposed = true
    for (const watchdog of this.watchdogs.values()) {
      clearTimeout(watchdog.timer)
    }
    this.watchdogs.clear()
    this.echoReconciliations.clear()
    this.exhausted.clear()
  }

  private scheduleWatchdog(rotationRequestUUID: string, delayMs: number, attempts: number): void {
    const timer = setTimeout(() => {
      void this.runWatchdog(rotationRequestUUID)
    }, delayMs)
    this.watchdogs.set(rotationRequestUUID, { attempts, timer })
  }

  private async runWatchdog(rotationRequestUUID: string): Promise<void> {
    if (this.disposed) return
    const watchdog = this.watchdogs.get(rotationRequestUUID)
    const lookup = this.getEntryByRequest(rotationRequestUUID)
    if (!watchdog || !lookup || lookup.entry.status !== 'reconciling') {
      this.cancelWatchdog(rotationRequestUUID)
      return
    }

    const attempts = watchdog.attempts + 1
    const refreshed = await this.refreshParent(lookup.entry)
    if (
      this.disposed ||
      this.watchdogs.get(rotationRequestUUID) !== watchdog ||
      !this.getEntryByRequest(rotationRequestUUID)
    ) {
      return
    }

    const serverStateConfirmed =
      lookup.entry.serverAcknowledged ||
      (refreshed && this.hasChangedMediaUrl(lookup.mediaObjectId, lookup.entry))

    if (serverStateConfirmed) {
      this.watchdogs.delete(rotationRequestUUID)
      this.clearForRequest(rotationRequestUUID)
      return
    }

    if (attempts >= this.maxAttempts) {
      this.watchdogs.delete(rotationRequestUUID)
      this.exhausted.add(rotationRequestUUID)
      return
    }

    this.scheduleWatchdog(rotationRequestUUID, this.retryMs, attempts)
  }

  private cancelWatchdog(rotationRequestUUID: string): void {
    const watchdog = this.watchdogs.get(rotationRequestUUID)
    if (watchdog) clearTimeout(watchdog.timer)
    this.watchdogs.delete(rotationRequestUUID)
  }

  private async refreshParent(entry: RotationReconciliationEntry): Promise<boolean> {
    if (!entry.reconcileQueryKey) return false

    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      const result = await Promise.race([
        this.queryClient.invalidateQueries({ queryKey: entry.reconcileQueryKey }).then(() => true),
        new Promise<boolean>(resolve => {
          timeout = setTimeout(() => resolve(false), this.refetchMaxWaitMs)
        }),
      ])
      return result
    } catch {
      return false
    } finally {
      if (timeout) clearTimeout(timeout)
    }
  }

  private hasChangedMediaUrl(mediaObjectId: string, entry: RotationReconciliationEntry): boolean {
    if (!entry.reconcileQueryKey) return false
    const originalSrc = getAssetUrl(entry.originalSrc)
    const matches = this.queryClient.getQueriesData({ queryKey: entry.reconcileQueryKey })

    for (const [, data] of matches) {
      const currentSrc = findMediaObjectSrc(data, mediaObjectId)
      if (currentSrc && getAssetUrl(currentSrc) !== originalSrc) return true
    }
    return false
  }
}

function findMediaObjectSrc(value: unknown, mediaObjectId: string): string | null {
  const stack: unknown[] = [value]
  const visited = new WeakSet<object>()

  while (stack.length > 0) {
    const candidate = stack.pop()
    if (typeof candidate !== 'object' || candidate === null) continue
    if (visited.has(candidate)) continue
    visited.add(candidate)

    if (Array.isArray(candidate)) {
      stack.push(...candidate)
      continue
    }

    const record = candidate as Record<string, unknown>
    if (isMediaObject(record, mediaObjectId)) {
      const src = record.contentUrlLarge ?? record.contentUrlMedium ?? record.contentUrl
      if (typeof src === 'string' && src !== '') return src
    }
    stack.push(...Object.values(record))
  }

  return null
}

function isMediaObject(record: Record<string, unknown>, mediaObjectId: string): boolean {
  const hasMediaUrl = ['contentUrlLarge', 'contentUrlMedium', 'contentUrl'].some(
    key => typeof record[key] === 'string',
  )
  if (!hasMediaUrl) return false
  if (String(record.id ?? '') === mediaObjectId) return true

  const iri = record['@id']
  if (typeof iri !== 'string') return false
  const match = /\/media_objects\/([^/?#]+)/.exec(iri)
  return match?.[1] === mediaObjectId
}
